<template>
  <div class="modern-master-view pb-12">
    <v-row class="mb-8 align-center px-4 pt-4">
      <div class="d-flex align-center">
        <v-avatar color="primary" size="48" class="mr-4 shadow-sm">
          <v-icon color="white">fas fa-file-invoice-dollar</v-icon>
        </v-avatar>
        <div>
          <div class="text-h4 font-weight-black primary--text lh-1">Cuentas por cobrar</div>
          <div class="caption grey--text font-weight-bold uppercase tracking-widest mt-1">
            Cartera Simple Factura · vencimientos, saldos y cobros
          </div>
        </div>
      </div>
      <v-spacer></v-spacer>
      <div class="d-flex align-center" style="gap: 12px;">
        <v-btn
          outlined
          color="primary"
          class="font-weight-black text-uppercase tracking-widest rounded-lg px-6"
          :loading="syncing"
          @click="sync"
        >
          <v-icon left small>fas fa-sync</v-icon> Sincronizar DTE
        </v-btn>
        <v-btn
          outlined
          color="primary"
          class="font-weight-black text-uppercase tracking-widest rounded-lg px-6"
          :loading="loading"
          @click="fetchAll"
        >
          <v-icon left small>fas fa-redo</v-icon> Actualizar
        </v-btn>
      </div>
    </v-row>

    <v-row class="mb-6 px-4">
      <v-col cols="12" sm="6" md="3">
        <v-card outlined class="modern-card pa-6">
          <div class="caption font-weight-black text-uppercase tracking-widest grey--text mb-1">Saldo abierto</div>
          <div class="text-h5 font-weight-black primary--text">{{ summary.open_balance | moneda }}</div>
          <div class="caption grey--text">{{ summary.open_count || 0 }} documentos</div>
        </v-card>
      </v-col>
      <v-col cols="12" sm="6" md="3">
        <v-card outlined class="modern-card pa-6">
          <div class="caption font-weight-black text-uppercase tracking-widest grey--text mb-1">Vencido</div>
          <div class="text-h5 font-weight-black error--text">{{ summary.overdue_balance | moneda }}</div>
          <div class="caption grey--text">{{ summary.overdue_count || 0 }} documentos</div>
        </v-card>
      </v-col>
      <v-col cols="12" sm="6" md="3">
        <v-card outlined class="modern-card pa-6">
          <div class="caption font-weight-black text-uppercase tracking-widest grey--text mb-1">Vence en 7 días</div>
          <div class="text-h5 font-weight-black warning--text">{{ summary.due_soon_balance | moneda }}</div>
          <div class="caption grey--text">{{ summary.due_soon_count || 0 }} documentos</div>
        </v-card>
      </v-col>
      <v-col cols="12" sm="6" md="3">
        <v-card outlined class="modern-card pa-6">
          <div class="caption font-weight-black text-uppercase tracking-widest grey--text mb-1">Antigüedad +90</div>
          <div class="text-h5 font-weight-black">{{ aging90.balance | moneda }}</div>
          <div class="caption grey--text">{{ aging90.count || 0 }} documentos</div>
        </v-card>
      </v-col>
    </v-row>

    <v-card outlined class="modern-card pa-6 mb-6 mx-4" v-if="(summary.aging || []).length">
      <div class="subtitle-2 font-weight-bold mb-3">Antigüedad de cartera</div>
      <v-row dense>
        <v-col v-for="bucket in summary.aging" :key="bucket.key" cols="6" md="2">
          <div class="caption grey--text">{{ bucket.label }}</div>
          <div class="font-weight-black">{{ bucket.balance | moneda }}</div>
          <div class="caption">{{ bucket.count }} docs</div>
        </v-col>
      </v-row>
    </v-card>

    <v-card outlined class="modern-card pa-6 mb-6 mx-4">
      <v-row dense align="center">
        <v-col cols="12" md="4">
          <v-text-field
            v-model="search"
            outlined dense hide-details
            label="Cliente / RUT / folio"
            prepend-inner-icon="fas fa-search"
            class="modern-input"
            @keyup.enter="fetchDocs"
          />
        </v-col>
        <v-col cols="12" md="3">
          <v-select
            v-model="status"
            :items="statusItems"
            item-text="text"
            item-value="value"
            outlined dense hide-details clearable
            label="Estado"
            class="modern-input"
            @change="fetchDocs"
          />
        </v-col>
        <v-col cols="12" md="2">
          <v-switch v-model="openOnly" label="Solo abiertos" hide-details class="mt-0" @change="fetchDocs" />
        </v-col>
        <v-col cols="12" md="2">
          <v-switch v-model="overdueOnly" label="Solo vencidos" hide-details class="mt-0" @change="fetchDocs" />
        </v-col>
      </v-row>
    </v-card>

    <v-card outlined class="modern-card mx-4">
      <v-data-table
        :headers="headers"
        :items="items"
        :loading="loading"
        :items-per-page="50"
        hide-default-footer
        class="elevation-0"
        no-data-text="Sin documentos. Emita facturas Simple Factura o sincronice DTE."
      >
        <template v-slot:item.receiver_name="{ item }">
          <div class="font-weight-bold">{{ item.receiver_name || '—' }}</div>
          <div class="caption grey--text">{{ item.receiver_tax_id }}</div>
        </template>
        <template v-slot:item.due_date="{ item }">
          <div>{{ formatDate(item.due_date) }}</div>
          <div v-if="item.days_overdue > 0" class="caption error--text">+{{ item.days_overdue }} d</div>
        </template>
        <template v-slot:item.total_amount="{ item }">{{ item.total_amount | moneda }}</template>
        <template v-slot:item.amount_paid="{ item }">{{ item.amount_paid | moneda }}</template>
        <template v-slot:item.amount_credited="{ item }">{{ item.amount_credited | moneda }}</template>
        <template v-slot:item.balance="{ item }">
          <span class="font-weight-black">{{ item.balance | moneda }}</span>
        </template>
        <template v-slot:item.status="{ item }">
          <v-chip x-small :color="statusColor(item.status)" class="font-weight-bold white--text">
            {{ item.status_label }}
          </v-chip>
        </template>
        <template v-slot:item.actions="{ item }">
          <v-menu offset-y left transition="slide-y-transition" rounded="lg">
            <template v-slot:activator="{ on, attrs }">
              <v-btn icon small v-bind="attrs" v-on="on">
                <v-icon small>fas fa-ellipsis-v</v-icon>
              </v-btn>
            </template>
            <v-list dense class="py-1 bespoke-menu">
              <v-list-item v-if="canPay(item)" @click.stop="openPay(item)" class="px-3 py-1">
                <v-list-item-icon class="mr-3 ml-0">
                  <v-icon small color="success">fas fa-hand-holding-usd</v-icon>
                </v-list-item-icon>
                <v-list-item-title class="body-2 font-weight-bold success--text">Cobrar</v-list-item-title>
              </v-list-item>
              <v-list-item @click.stop="openCostCenter(item)" class="px-3 py-1">
                <v-list-item-icon class="mr-3 ml-0">
                  <v-icon small color="primary">fas fa-sitemap</v-icon>
                </v-list-item-icon>
                <v-list-item-title class="body-2 font-weight-medium">Asignar centro de costo</v-list-item-title>
              </v-list-item>
              <v-list-item @click.stop="openCollection(item)" class="px-3 py-1">
                <v-list-item-icon class="mr-3 ml-0">
                  <v-icon small color="primary">fas fa-phone-volume</v-icon>
                </v-list-item-icon>
                <v-list-item-title class="body-2 font-weight-medium">Gestión de cobranza</v-list-item-title>
              </v-list-item>
              <v-divider v-if="canPay(item)" class="my-1"></v-divider>
              <v-list-item v-if="canPay(item)" @click.stop="markUncollectible(item)" class="px-3 py-1">
                <v-list-item-icon class="mr-3 ml-0">
                  <v-icon small color="error">fas fa-ban</v-icon>
                </v-list-item-icon>
                <v-list-item-title class="body-2 font-weight-medium error--text">Marcar incobrable</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </template>
      </v-data-table>
    </v-card>

    <v-dialog v-model="payDialog" max-width="480">
      <v-card v-if="payDoc">
        <v-card-title class="font-weight-black">Registrar cobro · folio {{ payDoc.folio }}</v-card-title>
        <v-card-text>
          <div class="mb-3 caption">Saldo {{ payDoc.balance | moneda }} · {{ payDoc.receiver_name }}</div>
          <v-text-field v-model="pay.amount" type="number" label="Monto" outlined dense />
          <v-text-field v-model="pay.payment_date" type="date" label="Fecha" outlined dense />
          <v-select v-model="pay.method" :items="methodItems" item-text="text" item-value="value" label="Medio" outlined dense />
          <v-select v-model="pay.bank_account_id" :items="accountItems" item-text="text" item-value="value" label="Cuenta tesorería" outlined dense clearable />
          <v-text-field v-model="pay.reference" label="Referencia / n° transferencia" outlined dense />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="payDialog = false">Cancelar</v-btn>
          <v-btn color="success" :loading="paying" @click="submitPay">Registrar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="collectionDialog" max-width="480">
      <v-card v-if="collectionDoc">
        <v-card-title class="font-weight-black">Gestión de cobranza</v-card-title>
        <v-card-text>
          <div class="mb-3 caption">Folio {{ collectionDoc.folio }} · {{ collectionDoc.receiver_name }}</div>
          <v-select v-model="collection.action_type" :items="actionItems" item-text="text" item-value="value" label="Tipo" outlined dense />
          <v-text-field v-model="collection.promise_date" type="date" label="Compromiso de pago" outlined dense clearable />
          <v-textarea v-model="collection.notes" label="Notas" outlined dense rows="3" />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="collectionDialog = false">Cancelar</v-btn>
          <v-btn color="primary" :loading="savingAction" @click="submitCollection">Guardar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="ccDialog" max-width="440">
      <v-card v-if="ccDoc">
        <v-card-title class="font-weight-black">Centro de costo · folio {{ ccDoc.folio }}</v-card-title>
        <v-card-text>
          <v-select v-model="ccId" :items="centerItems" item-text="text" item-value="value" label="Centro" outlined dense clearable />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="ccDialog = false">Cancelar</v-btn>
          <v-btn color="primary" :loading="savingCc" @click="submitCostCenter">Asignar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import notifications from '@/utils/notifications'
import {
  assignArCostCenter,
  createArPayment,
  createCollectionAction,
  fetchArDocuments,
  fetchArSummary,
  fetchCostCenters,
  fetchTreasuryAccounts,
  markArUncollectible,
  syncArFromDte,
} from '@/api/finance'

const STATUS_COLOR = {
  pending: 'info',
  partially_paid: 'warning',
  paid: 'success',
  overdue: 'error',
  cancelled: 'grey',
  uncollectible: 'grey darken-2',
}

export default {
  name: 'AccountsReceivable',
  data() {
    return {
      loading: false,
      syncing: false,
      paying: false,
      savingAction: false,
      search: '',
      status: '',
      openOnly: true,
      overdueOnly: false,
      items: [],
      summary: {},
      accounts: [],
      payDialog: false,
      payDoc: null,
      pay: { amount: '', payment_date: '', method: 'transfer', reference: '', bank_account_id: '' },
      collectionDialog: false,
      collectionDoc: null,
      collection: { action_type: 'call', promise_date: '', notes: '' },
      ccDialog: false,
      ccDoc: null,
      ccId: null,
      savingCc: false,
      costCenters: [],
      headers: [
        { text: 'Folio', value: 'folio' },
        { text: 'Tipo', value: 'document_kind_label' },
        { text: 'Cliente', value: 'receiver_name' },
        { text: 'Emisión', value: 'issue_date' },
        { text: 'Vencimiento', value: 'due_date' },
        { text: 'Total', value: 'total_amount', align: 'end' },
        { text: 'Pagado', value: 'amount_paid', align: 'end' },
        { text: 'NC', value: 'amount_credited', align: 'end' },
        { text: 'Saldo', value: 'balance', align: 'end' },
        { text: 'Centro', value: 'cost_center_code' },
        { text: 'Estado', value: 'status' },
        { text: '', value: 'actions', sortable: false, align: 'end', width: 56 },
      ],
    }
  },
  computed: {
    aging90() {
      const buckets = this.summary.aging || []
      return buckets.find(b => b.key === '90_plus') || { balance: 0, count: 0 }
    },
    statusItems() {
      const labels = this.summary.status_labels || {}
      return Object.keys(labels).map(value => ({ value, text: labels[value] }))
    },
    methodItems() {
      const labels = this.summary.payment_methods || { transfer: 'Transferencia' }
      return Object.keys(labels).map(value => ({ value, text: labels[value] }))
    },
    accountItems() {
      return this.accounts.map(acc => ({ value: acc.id, text: `${acc.code} · ${acc.name}` }))
    },
    centerItems() {
      return this.costCenters.map(c => ({ value: c.id, text: `${c.code} · ${c.name}` }))
    },
    actionItems() {
      const labels = this.summary.collection_action_types || {
        call: 'Llamada',
        email: 'Correo',
        whatsapp: 'WhatsApp',
        payment_promise: 'Compromiso de pago',
        note: 'Observación',
      }
      return Object.keys(labels).map(value => ({ value, text: labels[value] }))
    },
  },
  methods: {
    formatDate(value) {
      if (!value) return '—'
      const [y, m, d] = String(value).slice(0, 10).split('-')
      return `${d}/${m}/${y}`
    },
    statusColor(status) {
      return STATUS_COLOR[status] || 'grey'
    },
    canPay(item) {
      return item.balance > 0 &&
        !['cancelled', 'uncollectible', 'paid'].includes(item.status) &&
        item.document_kind !== 'credit_note'
    },
    async fetchAll() {
      await Promise.all([this.fetchSummary(), this.fetchDocs(), this.fetchAccounts(), this.fetchCenters()])
    },
    async fetchCenters() {
      try {
        const data = await fetchCostCenters()
        this.costCenters = data.items || []
      } catch (e) {
        this.costCenters = []
      }
    },
    async fetchAccounts() {
      try {
        const data = await fetchTreasuryAccounts()
        this.accounts = data.items || []
      } catch (e) {
        this.accounts = []
      }
    },
    async fetchSummary() {
      try {
        this.summary = await fetchArSummary()
      } catch (e) {
        notifications.showError('No se pudo cargar el resumen de CxC')
      }
    },
    async fetchDocs() {
      this.loading = true
      try {
        const data = await fetchArDocuments({
          search: this.search || undefined,
          status: this.status || undefined,
          open_only: this.openOnly,
          overdue_only: this.overdueOnly,
          page_size: 100,
        })
        this.items = data.items || []
      } catch (e) {
        notifications.showError('No se pudo cargar la cartera')
      } finally {
        this.loading = false
      }
    },
    async sync() {
      this.syncing = true
      try {
        const res = await syncArFromDte()
        notifications.showSuccess(`Sincronizados ${res.synced || 0} DTE (${res.errors || 0} errores)`)
        await this.fetchAll()
      } catch (e) {
        notifications.showError('No se pudo sincronizar')
      } finally {
        this.syncing = false
      }
    },
    openPay(item) {
      this.payDoc = item
      this.pay = {
        amount: item.balance,
        payment_date: new Date().toISOString().slice(0, 10),
        method: 'transfer',
        reference: '',
        bank_account_id: (this.accounts.find(a => a.is_default) || this.accounts[0] || {}).id || '',
      }
      this.payDialog = true
    },
    async submitPay() {
      const amount = Number(this.pay.amount)
      if (!amount || amount <= 0) {
        notifications.showError('Indique un monto válido')
        return
      }
      this.paying = true
      try {
        await createArPayment({
          client_id: this.payDoc.client_id,
          amount,
          payment_date: this.pay.payment_date,
          method: this.pay.method,
          reference: this.pay.reference || null,
          bank_account_id: this.pay.bank_account_id || null,
          applications: [{ ar_document_id: this.payDoc.id, amount }],
        })
        notifications.showSuccess('Cobro registrado')
        this.payDialog = false
        await this.fetchAll()
      } catch (e) {
        const msg = (e.response && e.response.data && e.response.data.error) || 'No se pudo registrar el cobro'
        notifications.showError(msg)
      } finally {
        this.paying = false
      }
    },
    openCollection(item) {
      this.collectionDoc = item
      this.collection = { action_type: 'call', promise_date: '', notes: '' }
      this.collectionDialog = true
    },
    async submitCollection() {
      this.savingAction = true
      try {
        await createCollectionAction({
          client_id: this.collectionDoc.client_id,
          ar_document_id: this.collectionDoc.id,
          action_type: this.collection.action_type,
          promise_date: this.collection.promise_date || null,
          notes: this.collection.notes,
        })
        notifications.showSuccess('Gestión registrada')
        this.collectionDialog = false
      } catch (e) {
        notifications.showError('No se pudo guardar la gestión')
      } finally {
        this.savingAction = false
      }
    },
    openCostCenter(item) {
      this.ccDoc = item
      this.ccId = item.cost_center_id || null
      this.ccDialog = true
    },
    async submitCostCenter() {
      this.savingCc = true
      try {
        await assignArCostCenter(this.ccDoc.id, this.ccId)
        notifications.showSuccess('Centro asignado')
        this.ccDialog = false
        await this.fetchDocs()
      } catch (e) {
        notifications.showError((e.response && e.response.data && e.response.data.error) || 'No se pudo asignar')
      } finally {
        this.savingCc = false
      }
    },
    async markUncollectible(item) {
      if (!confirm(`¿Marcar folio ${item.folio} como incobrable?`)) return
      try {
        await markArUncollectible(item.id)
        notifications.showSuccess('Documento marcado incobrable')
        await this.fetchAll()
      } catch (e) {
        notifications.showError('No se pudo marcar incobrable')
      }
    },
  },
  created() {
    this.fetchAll()
  },
}
</script>
